import React from 'react';
import { Grid, Box } from '@mui/material';
import TransformationBase from './common/TransformationBase';
import { TransformationHeading } from './common/fields/Heading';
import { COMMON_STYLES, COMMON_VALIDATION } from './common/Constants';
import { SourceColumnField } from './common/fields/SourceColumnField';
import { OutputColumnField } from './common/fields/OutputColumnField';
import { CheckBoxField } from './common/fields/CheckBoxField';
import TransformationArrayField from './common/fields/TransformationArrayField';

const INITIALSTATE = {
  type: 'to_decimal',
  alias: '',
  df: '',
  transformations: [
    {
      sourceColumns: [''],
      outputColumn: '',
      params: { precision: 10, scale: 2 },
      transformInPlace: false,
    },
  ],
};

const validationSchema = {
  ...COMMON_VALIDATION,
  'transformations.params.precision': {
    required: true,
    validate: (value) => {
      if (value === '' || value === null || value === undefined) {
        return 'Precision is required';
      }
      if (Number.isNaN(value) || value <= 0) {
        return 'Precision must be a positive number';
      }
      if (!Number.isInteger(Number(value))) {
        return 'Precision must be an integer';
      }
      return '';
    },
  },
  'transformations.params.scale': {
    required: true,
    validate: (value, transform) => {
      if (value === '' || value === null || value === undefined) {
        return 'Scale is required';
      }
      if (Number.isNaN(value) || value < 0) {
        return 'Scale must be a non-negative number';
      }
      if (!Number.isInteger(Number(value))) {
        return 'Scale must be an integer';
      }
      if (Number(value) > Number(transform?.params?.precision || 0)) {
        return 'Scale cannot be greater than precision';
      }
      return '';
    },
  },
};

const renderTransformationFields = ({ formField, errors, setFieldValue, disableForm, headers }) => {
  const handleInPlaceChange = (index, value) => {
    const newTransforms = [...formField.transformations];
    newTransforms[index].transformInPlace = value;
    if (value) {
      newTransforms[index].outputColumn = '';
    }
    setFieldValue('transformations', newTransforms);
  };

  const renderField = ({ transform, index, headers, disableForm, errors }) => {
    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CheckBoxField
              checked={transform.transformInPlace}
              onChange={(value) => handleInPlaceChange(index, value)}
              disabled={disableForm}
            />
          </Grid>

          <Grid item {...COMMON_STYLES.gridItem}>
            <SourceColumnField
              helperText={
                errors?.sourceColumns ||
                (transform.transformInPlace
                  ? 'This column will be converted directly'
                  : 'Select column to convert to decimal')
              }
              value={transform.sourceColumns?.[0] || ''}
              onChange={(e) => {
                const newTransforms = [...formField.transformations];
                newTransforms[index].sourceColumns = [e.target.value];
                setFieldValue('transformations', newTransforms);
              }}
              headers={headers}
              disabled={disableForm}
              error={!!errors.sourceColumns}
            />
          </Grid>

          <Grid item {...COMMON_STYLES.gridItem}>
            <OutputColumnField
              value={transform.params?.precision || ''}
              onChange={(e) => {
                const newTransforms = [...formField.transformations];
                newTransforms[index].params = {
                  ...newTransforms[index].params,
                  precision: e.target.value,
                };
                setFieldValue('transformations', newTransforms);
              }}
              disabled={disableForm}
              label="Precision *"
              type="number"
              helperText={errors.params?.precision || 'Total number of digits'}
              error={!!errors.params?.precision}
            />
          </Grid>

          <Grid item {...COMMON_STYLES.gridItem}>
            <OutputColumnField
              value={transform.params?.scale || ''}
              onChange={(e) => {
                const newTransforms = [...formField.transformations];
                newTransforms[index].params = {
                  ...newTransforms[index].params,
                  scale: e.target.value,
                };
                setFieldValue('transformations', newTransforms);
              }}
              disabled={disableForm}
              label="Scale *"
              type="number"
              helperText={errors?.params?.scale || 'Digits after decimal point'}
              error={!!errors?.params?.scale}
            />
          </Grid>

          {!transform.transformInPlace && (
            <Grid item {...COMMON_STYLES.gridItem}>
              <OutputColumnField
                value={transform.outputColumn}
                onChange={(e) => {
                  const newTransforms = [...formField.transformations];
                  newTransforms[index].outputColumn = e.target.value;
                  setFieldValue('transformations', newTransforms);
                }}
                disabled={disableForm}
                error={!!errors?.outputColumn}
                helperText={errors?.outputColumn || 'Enter output column name'}
              />
            </Grid>
          )}
        </Grid>
      </>
    );
  };

  return (
    <Box sx={COMMON_STYLES.container}>
      <TransformationHeading title='To Decimal - Convert to decimal number ("3.14" → 3.14)' disabled={disableForm} />
      <TransformationArrayField
        transformations={formField.transformations}
        setTransformations={(newTransforms) => setFieldValue('transformations', newTransforms)}
        renderField={renderField}
        errors={errors.transformations || []}
        headers={headers}
        disableForm={disableForm}
      />
    </Box>
  );
};

const NodeModal_ToDecimal = (props) => (
  <TransformationBase
    {...props}
    INITIALSTATE={INITIALSTATE}
    validationSchema={validationSchema}
    renderTransformationFields={renderTransformationFields}
    transformationType={INITIALSTATE.type}
    tranformationName="To Decimal"
  />
);

export default NodeModal_ToDecimal;
